import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchReaderBook, getCachedReaderBook, invalidateReaderBook, ReaderBook } from '@/services/booksApi';

const SUPPORTED_RENDER_PROFILE = 'chapter-fragments-v1';

export interface UseReaderBookResult {
  book: ReaderBook | null;
  isLoading: boolean;
  error: string | null;
  isRenderProfileSupported: boolean;
  retry: () => void;
}

export const useReaderBook = (bookId: string): UseReaderBookResult => {
  const [loadedBook, setLoadedBook] = useState<ReaderBook | null>(() =>
    bookId ? getCachedReaderBook(bookId) : null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const retry = useCallback(() => {
    if (bookId) invalidateReaderBook(bookId);
    setRetryVersion((value) => value + 1);
  }, [bookId]);
  const book = loadedBook?.id === bookId
    ? loadedBook
    : (bookId ? getCachedReaderBook(bookId) : null);

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    const loadBook = async () => {
      if (!bookId) {
        setLoadedBook(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(!getCachedReaderBook(bookId));
        setError(null);
        const readerBook = await fetchReaderBook(bookId, abortController.signal);
        if (cancelled) {
          return;
        }
        setLoadedBook(readerBook);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) {
          return;
        }
        setLoadedBook(null);
        setError(err instanceof Error ? err.message : 'Failed to load reader book');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadBook();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [bookId, retryVersion]);

  const isRenderProfileSupported = useMemo(() => {
    if (!book) {
      return false;
    }
    const supported =
      book.renderProfile === SUPPORTED_RENDER_PROFILE &&
      book.ebook.format === SUPPORTED_RENDER_PROFILE;
    if (!supported) {
      console.warn(
        `[reader] Unsupported reader format '${book.renderProfile}/${book.ebook.format}' for book '${book.slug}'.`
      );
    }
    return supported;
  }, [book]);

  return {
    book,
    isLoading,
    error,
    isRenderProfileSupported,
    retry,
  };
};
